import { LoadingOutlined } from '@ant-design/icons'
import * as Sentry from '@sentry/remix'
import { Button, Flex, Result, Typography } from 'antd'
import React, { ReactNode } from 'react'

const RETRY_KEY = '__error_retry_count__'
const MAX_RETRIES = 10

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showFinalError: boolean
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      showFinalError: false,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      showFinalError: false,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught in ErrorBoundary: ', error, errorInfo)

    const currentRetries = parseInt(localStorage.getItem(RETRY_KEY) || '0', 10)

    if (currentRetries === 0) {
      Sentry.captureException(error)
    }

    localStorage.setItem(RETRY_KEY, String(currentRetries + 1))

    if (currentRetries < MAX_RETRIES) {
      localStorage.setItem(RETRY_KEY, String(currentRetries + 1))
      setTimeout(() => {
        // window.location.reload()
      }, 1000)
    } else if (currentRetries === MAX_RETRIES) {
      this.handleGoHome()
    } else {
      this.setState({ showFinalError: true })
    }
  }

  handleReload = () => {
    localStorage.removeItem(RETRY_KEY)
    window.location.reload()
  }

  handleGoHome = () => {
    localStorage.removeItem(RETRY_KEY)
    window.location.replace('/')
  }

  render() {
    const { hasError, error, showFinalError } = this.state

    if (hasError && error) {
      if (!showFinalError) {
        return (
          <Flex
            style={{ height: '100vh' }}
            align="center"
            justify="center"
            vertical
          >
            <LoadingOutlined />
            <Typography.Text className="my-4">
              Hang tight, trying to reconnect...
            </Typography.Text>

            <Button type="primary" onClick={this.handleGoHome}>
              Refresh
            </Button>
          </Flex>
        )
      }

      return (
        <Flex className="w-full h-full pt-20" align="center" justify="center">
          <Result
            status="500"
            title="Something went wrong"
            subTitle="There was an unexpected error in the application."
            style={{ background: 'white' }}
            extra={
              <Button type="primary" onClick={this.handleGoHome}>
                Back Home
              </Button>
            }
          />
        </Flex>
      )
    }

    return this.props.children
  }
}
